// Normalises any thrown value into a typed `AppError`. The two axios
// instances in `httpClient.ts` run their response interceptors through this
// function so feature code can rely on `catch (e) { … }` always producing
// the same shape.
//
// The fallback message must never be a user-facing surprise — it appears
// when the wire format is unrecognised, so keep it generic and benign.

import axios, { type AxiosError } from 'axios'

export interface AppError {
  message: string
  statusCode?: number
  errorCode?: string
  details?: unknown
}

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred.'

const isAppError = (error: unknown): error is AppError =>
  !!error &&
  typeof error === 'object' &&
  !axios.isAxiosError(error) &&
  !(error instanceof Error) &&
  'message' in error &&
  typeof (error as { message: unknown }).message === 'string'

// Pulls the most caller-friendly message out of an axios error: API body
// `message` / `error` field first, then axios's own message, then the
// generic fallback. Extracted so `normalizeError` stays at one branch per
// error kind.
const extractAxiosMessage = (error: AxiosError): string => {
  const data = error.response?.data
  if (data && typeof data === 'object') {
    if ('message' in data && typeof data.message === 'string') return data.message
    if ('error' in data && typeof data.error === 'string') return data.error
  }
  return error.message || DEFAULT_ERROR_MESSAGE
}

export const normalizeError = (error: unknown): AppError => {
  if (isAppError(error)) return error

  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    const rawCode =
      data && typeof data === 'object' && 'errorCode' in data ? data.errorCode : undefined
    return {
      message: extractAxiosMessage(error),
      statusCode: error.response?.status,
      errorCode: typeof rawCode === 'string' ? rawCode : undefined,
      details: data,
    }
  }

  if (error instanceof Error) {
    return { message: error.message }
  }

  return { message: DEFAULT_ERROR_MESSAGE }
}
